import React, { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  hue: number;
}

interface FloatingParticlesProps {}

const FloatingParticles: React.FC<FloatingParticlesProps> = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);
  
  // Create a new particle
  const createParticle = useCallback((canvas: HTMLCanvasElement): Particle => {
    // Biased towards smaller particles with occasional medium and large ones
    let size;
    const sizeRand = Math.random();
    if (sizeRand < 0.03) { // 3% chance for larger particles
      size = Math.random() * 1.2 + 1.8; // 1.8-3.0
    } else if (sizeRand < 0.15) { // 12% chance for medium
      size = Math.random() * 0.7 + 1.1; // 1.1-1.8
    } else { // 85% small particles
      size = Math.random() * 0.6 + 0.5; // 0.5-1.1
    }
    
    // More subtle speeds
    const speedMultiplier = 0.6;
    return {
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size,
      speedX: (Math.random() - 0.5) * speedMultiplier,
      speedY: (Math.random() - 0.5) * speedMultiplier,
      opacity: Math.random() * 0.25 + 0.1, // More subtle opacity range: 0.1-0.35
      hue: Math.round(Math.random() * 60) + 180, // Blue to purple range
    };
  }, []);
  
  // Update particle position and state
  const updateParticle = useCallback((particle: Particle, canvas: HTMLCanvasElement) => {
    // Regular movement
    particle.x += particle.speedX;
    particle.y += particle.speedY;
    
    // Wrap around edges
    if (particle.x < 0) particle.x = canvas.width;
    if (particle.x > canvas.width) particle.x = 0;
    if (particle.y < 0) particle.y = canvas.height;
    if (particle.y > canvas.height) particle.y = 0;
  }, []);
  
  // Draw particle on canvas with improved blending
  const drawParticle = useCallback((particle: Particle, ctx: CanvasRenderingContext2D) => {
    ctx.save();
    
    // Set global blending for smoother appearance
    ctx.globalCompositeOperation = 'lighter';
    
    // Regular floating particle with improved blending
    // Uses radial gradients instead of layered circles for natural glow
    const radius = particle.size * 3.5; // Increased visibility
    const gradient = ctx.createRadialGradient(
      particle.x, particle.y, 0,
      particle.x, particle.y, radius
    );
    
    // Very subtle cores with naturally fading edges
    gradient.addColorStop(0, `hsla(${particle.hue}, 70%, 80%, ${particle.opacity * 0.9})`);
    gradient.addColorStop(0.3, `hsla(${particle.hue}, 70%, 70%, ${particle.opacity * 0.4})`);
    gradient.addColorStop(1, `hsla(${particle.hue}, 70%, 60%, 0)`);
    
    ctx.beginPath();
    ctx.fillStyle = gradient;
    ctx.arc(particle.x, particle.y, radius, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.restore();
  }, []);
  
  // Initialize particles
  useEffect(() => {
    // Force reinitialize by clearing initializedRef
    initializedRef.current = false;
    
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const handleResize = () => {
      if (!canvas) return;
      
      // Set canvas dimensions
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Create new particles if we don't have any yet or we need to reinitialize
      if (particlesRef.current.length === 0 || !initializedRef.current) {
        const particles: Particle[] = [];
        
        // Calculate number of particles based on screen size
        // More particles for larger screens, but with a reasonable cap
        const screenArea = canvas.width * canvas.height;
        const particleCount = Math.min(Math.floor(screenArea / 9500), 125); // Slightly reduced particle count
        
        console.log(`Creating ${particleCount} particles`);
        
        for (let i = 0; i < particleCount; i++) {
          particles.push(createParticle(canvas));
        }
        
        particlesRef.current = particles;
        initializedRef.current = true;
      }
    };
    
    // Set initial size and handle window resizing
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Animation loop
    const animate = () => {
      if (!canvas || !ctx) return;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      for (const particle of particlesRef.current) {
        updateParticle(particle, canvas);
        drawParticle(particle, ctx);
      }
      
      // Request next frame
      animationRef.current = requestAnimationFrame(animate);
    };
    
    // Start animation
    animate();
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [createParticle, drawParticle, updateParticle]);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 w-full h-full pointer-events-none"
    />
  );
};

export default FloatingParticles; 